import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';

import {
  buildDefaultRequestReason,
  buildRequestRoleUrl,
  submitRoleRequest,
} from '../utils/permissionErrors';

/**
 * RequestAccessDialog - Confirm and submit a role request without leaving the app.
 *
 * Opened by `PermissionRequestProvider` when the user clicks "Request access"
 * on a permission-denied toast or banner. On confirm it POSTs to the app's own
 * `/request-role` endpoint, which writes a PENDING row and emails the
 * permissions admins.
 *
 * Not built on `ConfirmationDialog`: that component closes as soon as confirm
 * fires and has no room for an input or a pending/error state, so a failed
 * submit would disappear with no way to retry.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Called on cancel, backdrop click, or after a successful submit
 * @param {Object} props.api - App transport from `createAppTransport`
 * @param {string} props.appName - App slug, used in the default reason
 * @param {string[]} [props.roles=[]] - Role ids the backend said would grant access
 * @param {Function} [props.onSubmitted] - Called with `{roleId, alreadyPending, alreadyGranted}` after success
 *
 * @example
 * <RequestAccessDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   api={api}
 *   appName="recon"
 *   roles={['recon_editor']}
 *   onSubmitted={({ alreadyPending }) => showNotification(...)}
 * />
 */
export const RequestAccessDialog = ({
  open,
  onClose,
  api,
  appName,
  roles = [],
  onSubmitted,
}) => {
  const [roleId, setRoleId] = useState(roles[0] || '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Identifies the current open-dialog session. A POST captures it at submit
  // time and drops its result if it no longer matches, so an in-flight request
  // can never report into a session the user has left — whether that's a new
  // denial reopening the dialog mid-flight (a background poll's toast, another
  // tab, an app calling requestAccess() directly), the dialog closing, or the
  // whole thing unmounting.
  const session = useRef(0);

  const defaultReason = useMemo(() => buildDefaultRequestReason(appName), [appName]);

  // Each denial can name different roles, so reset when the dialog reopens
  // rather than showing the previous denial's role and any stale error.
  useEffect(() => {
    if (!open) return undefined;
    session.current += 1;
    setRoleId(roles[0] || '');
    setReason(defaultReason);
    setError(null);
    setSubmitting(false);
    // Cleanup ends the session — it runs on close, on a roles change, and on
    // unmount, so any request still in flight is invalidated in all three
    // cases rather than resolving into a dialog that has moved on (or gone).
    return () => {
      session.current += 1;
    };
    // roles is a fresh array each render; key off its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roles.join(','), defaultReason]);

  const handleSubmit = async () => {
    const mySession = session.current;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitRoleRequest({ api, roleId, reason, appName });
      if (mySession !== session.current) return;
      onSubmitted?.({
        roleId,
        alreadyPending: Boolean(result?.already_pending),
        alreadyGranted: Boolean(result?.already_granted),
      });
      onClose();
    } catch (err) {
      if (mySession !== session.current) return;
      // Stay open on failure so the reason the user typed isn't lost. Prefer
      // the server's human-readable ``message`` (an outside_org 403 carries a
      // full explanation) over the machine-readable ``error`` code.
      const body = err?.response?.data;
      setError(
        body?.message ||
          body?.error ||
          err?.message ||
          'Could not submit the request. Please try again.'
      );
      setSubmitting(false);
    }
  };

  // With no role named by the server there is nothing to submit, so send the
  // user to the Permissions app to pick one themselves.
  const canSubmit = Boolean(roleId) && !submitting;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request access</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {roles.length > 1
            ? 'Pick the role you need and send the request to a permissions administrator.'
            : 'This sends a request to a permissions administrator for review.'}
        </DialogContentText>

        {roles.length > 1 ? (
          <TextField
            select
            fullWidth
            label="Role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            disabled={submitting}
            sx={{ mb: 2 }}
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            fullWidth
            label="Role"
            value={roleId}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          helperText="Shown to the administrator reviewing your request."
        />

        {!roleId && (
          <Alert severity="info" sx={{ mt: 2 }}>
            We couldn&apos;t tell which role grants this action.{' '}
            <a href={buildRequestRoleUrl(null)} target="_blank" rel="noopener noreferrer">
              Open the Permissions app
            </a>{' '}
            to pick one.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          autoFocus
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Sending…
            </Box>
          ) : (
            'Send request'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestAccessDialog;
